import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { Course } from '@/types';
import { courseApi } from '@/services/api';
import { usePublicSearchStore } from '@/store/publicSearchStore';
import { CourseCard } from './components/CourseCard';
import { CategoryCardCarousel, groupItemsByCategory } from '@/components/public/CategoryCardCarousel';

export const CoursesPage = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const searchQuery = usePublicSearchStore((state) => state.queries.courses);
  const trimmedSearchQuery = searchQuery.trim();
  const deferredSearchQuery = useDeferredValue(trimmedSearchQuery);
  const normalizedSearchQuery = deferredSearchQuery.toLowerCase();
  const visibleCourses = normalizedSearchQuery
    ? courses.filter((course) => course.title.toLowerCase().includes(normalizedSearchQuery))
    : courses;
  const courseGroups = useMemo(() => groupItemsByCategory(visibleCourses), [visibleCourses]);
  useEffect(() => {
    const controller = new AbortController();

    const fetchCourses = async () => {
      setIsLoading(true);
      setError('');
      try {
        const data = await courseApi.getAllCourses(controller.signal);
        setCourses(data);
      } catch (fetchError) {
        if ((fetchError as Error).name !== 'AbortError') {
          setError('Courses are not available right now. Please try again soon.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    void fetchCourses();
    return () => controller.abort();
  }, []);

  return (
    <section className="courses-page min-h-full">
      <div className="courses-shell">
        {trimmedSearchQuery ? (
          <p className="courses-search-note">
            Showing results for <span className="text-slate-950">&quot;{searchQuery}&quot;</span>
          </p>
        ) : null}

        {isLoading ? (
          <div id="featured-courses" className="courses-grid">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
              <div key={item} className="courses-skeleton" />
            ))}
          </div>
        ) : error ? (
          <div className="courses-empty-state">
            {error}
          </div>
        ) : visibleCourses.length ? (
          <div id="featured-courses" className="space-y-8">
            {courseGroups.map((group) => (
              <CategoryCardCarousel
                key={group.id}
                title={group.name}
                itemCount={group.items.length}
                itemLabel="course"
              >
                {group.items.map((course) => (
                  <CourseCard key={course.id} course={course} />
                ))}
              </CategoryCardCarousel>
            ))}
          </div>
        ) : (
          <div className="courses-empty-state">
            {trimmedSearchQuery ? 'No courses matched your search.' : 'No courses found.'}
          </div>
        )}

      </div>
    </section>
  );
};
